import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { format } from "date-fns";
import * as orderService from "@/supabase/order/service/orderService";
import { useAuth } from "../contexts/AuthContext";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { logError } from '@/lib/sentry';

const DEFAULT_CUSTOMER_NICKNAME = "고객 닉네임";
const DEFAULT_CUSTOMER_STATIC_ID = "default_customer_placeholder_id"; // 고정 ID

export function useOrdersByDate() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [customerTemplates, _setCustomerTemplates] = useState<CustomerTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  
  // defaultCustomerId는 이제 selectedDate에 의존하지 않음
  // const defaultCustomerId = `default_${format(selectedDate, 'yyyyMMdd')}`;
  const defaultCustomerId = DEFAULT_CUSTOMER_STATIC_ID;
  
  const setCustomerTemplates = useCallback((updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])) => {
    if (typeof updater === 'function') {
      _setCustomerTemplates(prev => {
        const newState = updater(prev);
        return newState;
      });
    } else {
      _setCustomerTemplates(updater);
    }
  }, []);
  
  const updateDefaultCustomerNickname = useCallback((newNickname: string, customerIdToUpdate?: string) => {
    setCustomerTemplates(current => {
      return current.map(template => {
        const isSpecificCustomerToUpdate = customerIdToUpdate && template.id === customerIdToUpdate;
        const isGenericDefaultCustomer = !customerIdToUpdate && (template.id.startsWith('default_') || template.nickname === DEFAULT_CUSTOMER_NICKNAME);

        if (isSpecificCustomerToUpdate || isGenericDefaultCustomer) {
          return { ...template, nickname: newNickname };
        }
        return template;
      });
    });
  }, [setCustomerTemplates]);
  
  const createDefaultCustomerTemplate = useCallback((id: string): CustomerTemplate => {
    return {
      id,
      nickname: DEFAULT_CUSTOMER_NICKNAME,
      template: "",
      items: [],
      is_keep: false,
      status: "미지정",
      memo: "",
      createdAt: new Date().toISOString(),
      totalPrice: 0,
      paymentMethod: null,
      order_date: format(selectedDate, 'yyyy-MM-dd')
    }
  }, [selectedDate]);

  const addDefaultCustomerIfNeeded = useCallback((): string | null => {
    const defaultExists = customerTemplates.some(template => 
      template.id === defaultCustomerId || 
      template.id.startsWith('default_')
    );
    if (!defaultExists) {
      const newDefaultCustomer = createDefaultCustomerTemplate(defaultCustomerId);
      setCustomerTemplates(current => [...current, newDefaultCustomer]);
      return defaultCustomerId;
    }
    return null;
  }, [customerTemplates, defaultCustomerId, createDefaultCustomerTemplate, setCustomerTemplates]);
  
  const getDefaultCustomer = useCallback((): CustomerTemplate | null => {
    const found = customerTemplates.find(template => 
      template.id === defaultCustomerId || template.id.startsWith('default_')
    ) || null;
    return found;
  }, [customerTemplates, defaultCustomerId]);

  // ★stale-응답 가드(codexloop R1/R2/R3): 초기 fetch와 refreshOrders가 '한 생성 카운터'를 공유한다.
  //   확정 직후 '되돌리기' 클릭·날짜 변경 등으로 요청이 겹칠 때, 먼저 시작한 응답이 늦게 와서
  //   최신 상태를 덮어쓰는 것을 막는다(소스 무관 마지막 호출만 반영). isMounted는 언마운트 방어로 병행.
  const refreshReqIdRef = useRef(0);
  // ★스코프 가드(R3): reqId 'latest-wins'만으론 부족 — 날짜를 D2로 바꾼 뒤 D1 클로저(예: 진행 중이던
  //   confirm이 잡고 있던 D1 refreshOrders)가 늦게 재개되면 그게 최신 reqId가 되어 D1 주문을 D2 화면에
  //   설치한다. 그래서 호출자의 캡처 스코프(user|date)가 현재와 다르면 reqId를 건드리기 전에 폐기한다.
  //   ★latest-ref 패턴(R4): 렌더 중 ref 쓰기는 concurrent 렌더에서 미커밋 스코프 노출 위험 →
  //   커밋 후 useLayoutEffect로 갱신(deps 없음 = 매 커밋, promise 콜백/유저 이벤트 전에 반영).
  const currentScope = `${user?.id ?? ''}|${format(selectedDate, 'yyyy-MM-dd')}`;
  const currentScopeRef = useRef(currentScope);
  useLayoutEffect(() => { currentScopeRef.current = currentScope; });

  useEffect(() => {
    let isMounted = true;
    const fetchInitialOrders = async () => {
      const reqId = ++refreshReqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        let templates: CustomerTemplate[] = [];
        if (user) {
          const ordersFromDB = await orderService.findByUserAndDateWithItems(
            user.id,
            format(selectedDate, 'yyyy-MM-dd')
          );

          templates = ordersFromDB.map((order) => {
            const items = (order.items ?? []).map(it => ({
              name: it.item_name,
              price: it.price,
              quantity: it.quantity
            }));
            const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            return {
              id: `order_${order.id}`,
              nickname: order.nickname,
              template: "", // Assuming template is not directly on order, or needs separate fetching
              items,
              is_keep: order.is_keep ?? false,
              status: order.status ?? "미지정",
              memo: order.memo ?? "",
              createdAt: order.created_at,
              totalPrice,
              shippingFeeOverride: order.shipping_fee_override ?? null,
              paymentMethod: order.payment_method ?? null,
              order_date: order.order_date
            };
          });

          if (templates.length === 0) {
            templates.push(createDefaultCustomerTemplate(defaultCustomerId));
          }
        } else {
          templates = [createDefaultCustomerTemplate(defaultCustomerId)];
        }
        
        if (isMounted && reqId === refreshReqIdRef.current) {
          setCustomerTemplates(templates);
        }
      } catch (err) {
        logError("[useOrdersByDate] fetchInitialOrders 실패:", err);
        if (isMounted && reqId === refreshReqIdRef.current) {
          setError(err as Error);
          setCustomerTemplates([]);
        }
      } finally {
        // 이 초기 fetch가 최신일 때만 스피너 해제(reqId 가드). superseding refresh가 끼어들면
        // 그 refresh가 끝나며 해제한다(refreshOrders finally) — discarded 초기 fetch가 스피너를
        // 일찍 숨기는 것 방지(codexloop R3).
        if (isMounted && reqId === refreshReqIdRef.current) {
          setLoading(false);
        }
      }
    };
    
    fetchInitialOrders();
    
    return () => {
      isMounted = false;
    };
  }, [user, selectedDate, defaultCustomerId, createDefaultCustomerTemplate, setCustomerTemplates]);

  const refreshOrders = useCallback(async () => {
    if (!user) {
      // console.log("[useOrdersByDate] refreshOrders - 비회원, 실행 안 함.");
      return;
    }
    // ★스코프 가드(R3): 이 클로저가 캡처한 user|date가 현재와 다르면(예: 날짜를 바꾼 뒤 늦게 재개된
    //   D1 refreshOrders) reqId를 건드리지 않고 즉시 폐기 — D1 주문이 D2 화면에 설치되는 것 방지.
    const myScope = `${user.id}|${format(selectedDate, 'yyyy-MM-dd')}`;
    if (myScope !== currentScopeRef.current) return;
    const reqId = ++refreshReqIdRef.current;
    // console.log(`[useOrdersByDate] refreshOrders 시작. user: ${user.id}, selectedDate: ${format(selectedDate, 'yyyy-MM-dd')}, defaultCustomerId: ${defaultCustomerId}`);
    setError(null);
    try {
      const ordersFromDB = await orderService.findByUserAndDateWithItems(
        user.id,
        format(selectedDate, 'yyyy-MM-dd')
      );
      // 최신 reqId가 아니거나(동일 스코프 후속 refresh) 스코프가 바뀌었으면(날짜/유저 이동) 폐기.
      if (reqId !== refreshReqIdRef.current || myScope !== currentScopeRef.current) return;
      // console.log(`[useOrdersByDate] refreshOrders - DB에서 가져온 ordersFromDB (${ordersFromDB.length}개):`, JSON.stringify(ordersFromDB.map(o => ({id: o.id, nickname: o.nickname}))));

      let newTemplates: CustomerTemplate[] = ordersFromDB.map((order) => {
        const items = (order.items ?? []).map(it => ({
          name: it.item_name,
          price: it.price,
          quantity: it.quantity
        }));
        const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        return {
          id: `order_${order.id}`,
          nickname: order.nickname,
          template: "", // Assuming template is not directly on order, or needs separate fetching
          items,
          is_keep: order.is_keep ?? false,
          status: order.status ?? "미지정",
          memo: order.memo ?? "",
          createdAt: order.created_at,
          totalPrice,
          shippingFeeOverride: order.shipping_fee_override ?? null,
          paymentMethod: order.payment_method ?? null,
          order_date: order.order_date
        };
      });
      // console.log(`[useOrdersByDate] refreshOrders - DB 변환 후 newTemplates (${newTemplates.length}개):`, JSON.stringify(newTemplates.map(t => ({id: t.id, nickname: t.nickname, itemsCount: t.items.length}))));
      
      // DB에서 가져온 고객 목록이 완전히 비어있을 경우에만 기본 고객 추가
      if (newTemplates.length === 0) {
        // console.log("[useOrdersByDate] refreshOrders - newTemplates가 비어있음. defaultCustomerId로 기본 고객 추가.", defaultCustomerId);
        newTemplates.push(createDefaultCustomerTemplate(defaultCustomerId)); // defaultCustomerId는 이미 DEFAULT_CUSTOMER_STATIC_ID를 사용
      }
      
      const sortedTemplates = newTemplates.sort((a, b) => {
        if (a.id === DEFAULT_CUSTOMER_STATIC_ID && b.id !== DEFAULT_CUSTOMER_STATIC_ID) return 1;
        if (a.id !== DEFAULT_CUSTOMER_STATIC_ID && b.id === DEFAULT_CUSTOMER_STATIC_ID) return -1;
        const orderIdA = parseInt(a.id.replace('order_', ''), 10);
        const orderIdB = parseInt(b.id.replace('order_', ''), 10);
        if (!isNaN(orderIdA) && !isNaN(orderIdB)) return orderIdA - orderIdB;
        return 0;
      });
      // console.log(`[useOrdersByDate] refreshOrders - 최종 setCustomerTemplates 호출 전 sortedTemplates (${sortedTemplates.length}개):`, JSON.stringify(sortedTemplates.map(t => ({id: t.id, nickname: t.nickname, itemsCount: t.items.length}))));
      setCustomerTemplates(sortedTemplates);

    } catch (err) {
      if (reqId !== refreshReqIdRef.current || myScope !== currentScopeRef.current) return; // stale 실패도 최신 상태를 덮어쓰지 않게
      logError("[useOrdersByDate] refreshOrders 실패:", err);
      setError(err as Error);
      setCustomerTemplates([]);
    } finally {
      // superseding current refresh가 진행 중이던 초기 로딩 스피너를 해제하게 한다(codexloop R3).
      // 폐기 스코프/구식 reqId면 건드리지 않는다(최신 fetch가 책임).
      if (reqId === refreshReqIdRef.current && myScope === currentScopeRef.current) {
        setLoading(false);
      }
    }
  }, [user, selectedDate, defaultCustomerId, createDefaultCustomerTemplate, setCustomerTemplates, setError, setLoading]);

  const removeOrderAndUpdateTemplates = useCallback(async (orderIdToRemove: number) => {
    try {
      await orderService.removeOrder(orderIdToRemove);
      await refreshOrders(); 
      return true; 
    } catch (err) {
      logError(`[useOrdersByDate] removeOrderAndUpdateTemplates - 주문 삭제 실패 (orderId: ${orderIdToRemove}):`, err);
      alert("주문 삭제에 실패했습니다. 다시 시도해주세요.");
      setError(err as Error);
      return false; 
    }
  }, [refreshOrders, setError]);

  const removeLocalCustomerOnly = useCallback((customerIdToRemove: string) => {
    setCustomerTemplates(prevTemplates => {
      let updated = prevTemplates.filter(t => t.id !== customerIdToRemove);
      
      if (user && updated.length === 0) {
        updated.push(createDefaultCustomerTemplate(defaultCustomerId));
      }
 
      return updated.sort((a, b) => { 
        if (a.id.startsWith('default_')) return 1;
        if (b.id.startsWith('default_')) return -1;
        const orderIdA = parseInt(a.id.replace('order_', ''), 10);
        const orderIdB = parseInt(b.id.replace('order_', ''), 10);
        if (!isNaN(orderIdA) && !isNaN(orderIdB)) return orderIdA - orderIdB;
        return 0;
      });
    });
  }, [user, createDefaultCustomerTemplate, defaultCustomerId, setCustomerTemplates]);

  const updateCustomerNicknameInTemplates = useCallback((customerId: string, newNickname: string) => {
    setCustomerTemplates(prevTemplates =>
      prevTemplates.map(template =>
        template.id === customerId
          ? { ...template, nickname: newNickname }
          : template
      )
    );
  }, [setCustomerTemplates]);

  return {
    selectedDate,
    setSelectedDate,
    customerTemplates,
    loading,
    error,
    refreshOrders,
    updateDefaultCustomerNickname,
    addDefaultCustomerIfNeeded,
    getDefaultCustomer,
    removeOrderAndUpdateTemplates,
    removeLocalCustomerOnly,
    updateCustomerNicknameInTemplates
  };
}